import axios, { AxiosInstance, AxiosError } from 'axios';

const configuredApiUrl = (process.env.REACT_APP_API_URL || '').trim();
const isLocalBrowserHost =
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

let apiBaseUrl = configuredApiUrl || '/';

// Prevent production/deployed UIs from accidentally trying localhost APIs.
if (!isLocalBrowserHost && /localhost|127\.0\.0\.1/i.test(apiBaseUrl)) {
  apiBaseUrl = '/';
}

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    const requestedUrl = (response.config?.url || '').toString();
    const contentType = (response.headers?.['content-type'] || '').toString().toLowerCase();

    // If an API call gets HTML back, it usually means the request hit the frontend app instead of FastAPI.
    if (requestedUrl.startsWith('/') && contentType.includes('text/html')) {
      return Promise.reject(
        new AxiosError(
          'API returned HTML instead of JSON. Check REACT_APP_API_URL or dev proxy configuration.',
          'ERR_BAD_RESPONSE',
          response.config,
          response.request,
          response
        )
      );
    }

    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      
      // Handle specific error codes
      switch (status) {
        case 401:
          // Unauthorized - clear stale token and let pages handle guest UX.
          localStorage.removeItem('token');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('ecopredict-auth-token-changed'));
          }
          break;
        case 429:
          // Too many requests
          console.error('Rate limit exceeded. Please try again later.');
          break;
        case 500:
          // Server error
          console.error('Server error. Please try again later.');
          break;
        default:
          console.error('API error:', error.response.data);
      }
    } else if (error.request) {
      // Network error
      console.error('Network error. Backend API is unreachable.');
      const base = (error.config?.baseURL || apiBaseUrl || 'unknown').toString();
      error.message = `Cannot reach backend API (${base}). Start backend on port 8000 or set REACT_APP_API_URL.`;
    }
    
    return Promise.reject(error);
  }
);

export default api;
